import { useContext, useState } from "react";
import { IconButton, Tooltip, Tabs, Tab, Select, MenuItem } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MUICard from "../../../Component/UI/MUICard/MUICard";
import Modal from "../../../Component/Modal/Modal";
import Sources from "../../../Component/UI/MUIToolTip/Sources";
import { DiscoveryContext } from "../discovery-context";
import ErrorView from "../../../Component/ErrorView/ErrorView";
import LoadingSkeleton from "./LoadingSkeleton";
import classes from "./NonBrandedQuestions.module.css";

const QUESTIONS_TYPE_OPTIONS = [
  { value: "open-ended", label: "Open Ended Questions" },
  { value: "fact-based", label: "Fact Based Questions" },
];

// baseResponses is a flat array ({basePrompt, baseResponse}) covering every execution
// across every question, in the same order as the question groups were chunked —
// slice it back into per-question chunks of the same size as each response group.
function chunkBaseResponses(baseResponses, groupSizes) {
  const chunks = [];
  let offset = 0;
  for (const size of groupSizes) {
    chunks.push(baseResponses.slice(offset, offset + size));
    offset += size;
  }
  return chunks;
}

export default function NonBrandedQuestions() {
  const discoveryCtx = useContext(DiscoveryContext);
  const transactionStatus = discoveryCtx.state?.transactionStatus;

  const hasNonBranded = !!discoveryCtx.isNonBrandedQuestions;
  const hasFactBased = !!discoveryCtx.isFactBasedQuestions;

  const [questionsType, setQuestionsType] = useState(hasNonBranded ? "open-ended" : "fact-based");
  const [activeQuestion, setActiveQuestion] = useState(0);
  const [promptModalOpen, setPromptModalOpen] = useState(false);

  if (!hasNonBranded && !hasFactBased) return null;

  const resultsForType = discoveryCtx.non_branded_questions_results?.[questionsType] || {};
  const questionGroups = resultsForType.meta || [];
  const baseResponses = resultsForType.baseResponses || [];

  const loading = questionGroups.length === 0 && transactionStatus === "RUNNING";
  const hasError = questionGroups.length === 0 && transactionStatus === "RETRY";

  const handleQuestionsTypeChange = (value) => {
    setQuestionsType(value);
    setActiveQuestion(0);
  };

  const baseResponseGroups = chunkBaseResponses(
    baseResponses,
    questionGroups.map((group) => group.length)
  );

  const activeGroup = questionGroups[activeQuestion] || [];
  // Same rendered prompt for every response of a given question — just show the first.
  const promptText = baseResponseGroups[activeQuestion]?.[0]?.basePrompt || "Base prompt not available";

  const cardTitle = (
    <div className={classes.title_row}>
      <span>Questions</span>
      {hasNonBranded && hasFactBased && (
        <Select
          size="small"
          value={questionsType}
          onChange={(e) => handleQuestionsTypeChange(e.target.value)}
          className={classes.type_select}
        >
          {QUESTIONS_TYPE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      )}
    </div>
  );

  return (
    <MUICard title={cardTitle} className={classes.card} titleClassName={classes.title}>
      {loading && !hasError && <LoadingSkeleton />}

      {!loading && !hasError && questionGroups.length === 0 && (
        <div className={classes.empty_state}>No questions configured for this type yet.</div>
      )}

      {!loading && !hasError && questionGroups.length > 0 && (
        <div className={classes.content}>
          <Tabs
            value={activeQuestion}
            onChange={(_, v) => setActiveQuestion(v)}
            variant="scrollable"
            scrollButtons="auto"
            className={classes.tabs}
          >
            {questionGroups.map((_, i) => (
              <Tab key={i} label={`Question ${i + 1}`} className={classes.tab} />
            ))}
          </Tabs>

          <div className={classes.panel}>
            <div className={classes.question_row}>
              <Tooltip title="View Base Prompt" arrow placement="top">
                <IconButton
                  size="small"
                  className={classes.question_info_btn}
                  onClick={() => setPromptModalOpen(true)}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <span className={classes.question_text}>{activeGroup?.[0]?.question}</span>
            </div>

            <div className={classes.responses_list}>
              {activeGroup.map((item, j) => (
                <div className={classes.response_block} key={j}>
                  <div className={classes.response_label}>Response {j + 1}</div>
                  <div className={classes.response_text}>{item.response}</div>
                  {item.sources?.length > 0 && (
                    <div className={classes.sources_block}>
                      <div className={classes.sources_label}>Sources</div>
                      <Sources
                        sources={item.sources.map((s) => ({
                          source: typeof s === "string" ? s : s?.url || "",
                          snippet: "",
                        }))}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {hasError && (
        <div className={classes.error_view}>
          <ErrorView title="Error Fetching Questions" message="Request Failed" />
        </div>
      )}

      <Modal
        open={promptModalOpen}
        handleClose={() => setPromptModalOpen(false)}
        title={`Question ${activeQuestion + 1} — Base Prompt`}
        width={700}
      >
        <div className={classes.prompt_modal_content}>{promptText}</div>
      </Modal>
    </MUICard>
  );
}
